/**
 * The corridors (tracks) a participant can be assigned, and where each lives in
 * the portal. One list, so the switcher, the gating and the post-login landing
 * cannot disagree about which corridors exist.
 *
 * Codes match the platform's Corridor enum (iips-platform-tenancy), except
 * Cross-border (XB): no operator assigns it as such. A participant holds it by
 * holding at least one scheme profile, on its ISO 20022 corridor, whose engine
 * serves the schemes.
 */
export const CORRIDORS = [
  { code: 'FSPIOP',   id: 'fspiop',   label: 'FSPIOP',       prefix: '',          home: '/dashboard' },
  { code: 'ISO20022', id: 'iso20022', label: 'ISO 20022',    prefix: '/iso20022', home: '/iso20022/dashboard' },
  { code: 'ISO8583',  id: 'iso8583',  label: 'ISO 8583',     prefix: '/iso8583',  home: '/iso8583/dashboard' },
  { code: 'XB',       id: 'xb',       label: 'Cross-border', prefix: '/xb',       home: '/xb/overview' },
];

/** What a session without a corridor list has always meant: the original two. */
const DEFAULT_ASSIGNED = ['FSPIOP', 'ISO20022'];

/**
 * The corridor codes a user is assigned, upper-cased. Cross-border is among
 * them when the user is on ISO 20022 and `schemes` (their scheme profile
 * assignments, once known) is not empty.
 */
export function assignedCorridors(user, schemes) {
  const platform = (user?.corridors || DEFAULT_ASSIGNED).map((c) => String(c).toUpperCase())
    .filter((c) => c !== 'XB');
  const crossBorder = platform.includes('ISO20022') && Array.isArray(schemes) && schemes.length > 0;
  return crossBorder ? [...platform, 'XB'] : platform;
}

/** The corridor a portal path belongs to. */
export function corridorForPath(pathname) {
  return CORRIDORS.find((c) => c.prefix && (pathname === c.prefix || pathname.startsWith(c.prefix + '/')))
    || CORRIDORS[0];
}

/**
 * Pages every corridor shows at the same address: the institution's users, its
 * settings and the password change. Users belong to the institution, not to a
 * corridor, so a participant on any mix of corridors manages them in one place.
 */
const SHARED_PREFIXES = ['/users', '/change-password'];

/** Whether a path is one of the pages every corridor shares. */
export function isSharedPath(pathname) {
  return SHARED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

const LAST_CORRIDOR_KEY = 'portal.lastCorridor';

/** Notes the corridor the reader is in, so a shared page can keep its sidebar. */
export function rememberCorridor(id) {
  try { sessionStorage.setItem(LAST_CORRIDOR_KEY, id); } catch { /* storage unavailable */ }
}

/**
 * The corridor a shared page belongs to: the one the reader came from, if it is
 * still theirs, or else their first. Without it a shared page would always show
 * the FSPIOP sidebar, even to a participant who does not hold FSPIOP.
 */
export function corridorForSharedPage(user, schemes) {
  const assigned = assignedCorridors(user, schemes);
  let last = null;
  try { last = sessionStorage.getItem(LAST_CORRIDOR_KEY); } catch { /* storage unavailable */ }
  return CORRIDORS.find((c) => c.id === last && assigned.includes(c.code))
    || CORRIDORS.find((c) => assigned.includes(c.code))
    || CORRIDORS[0];
}

/** Where a user lands: the first of their corridors, in the order above. */
export function homeFor(user) {
  const assigned = assignedCorridors(user);
  return (CORRIDORS.find((c) => assigned.includes(c.code)) || CORRIDORS[0]).home;
}
